/*
 * <product-packages> -- quantity-tier package selector for the product page.
 *
 * Picking a tier reveals one option row per item in that package, so every item
 * gets its own size (and any other product option). Prices are recomputed from
 * the variants actually chosen: unchosen items fall back to the cheapest
 * available variant so the tier still shows a meaningful "from" total.
 *
 * A package is several distinct variants, so it is added through /cart/add.js
 * with an `items` array rather than the product form.
 *
 * Prices here are never discounted: a tier quotes exactly what its items cost.
 * Any percentage off is a Shopify automatic discount, which applies in the cart,
 * and taking it off here as well would quote a price the checkout never charges.
 *
 * In the default "theme" button mode there is no button of its own: the package
 * total is written into the section's <product-submit-price> (whose own render()
 * then keeps multiplying it by the quantity input, so quantity 2 means two
 * packages), and the form submit is caught in the CAPTURE phase on the
 * <product-form> ancestor. Capturing on the ancestor is what makes the order
 * reliable -- it runs before <product-form>'s own listener on the <form>, and
 * stopPropagation() keeps the event from ever reaching it.
 */
if (!customElements.get('product-packages')) {
  // Joins option values into a lookup key. A control character, so it cannot
  // collide with an option value the merchant typed ("AB"+"C" and "A"+"BC" must
  // not produce the same key).
  const SEPARATOR = String.fromCharCode(31);

  class ProductPackages extends HTMLElement {
    connectedCallback() {
      // Every listener is registered against this signal, so disconnecting
      // drops all of them at once -- including the one on <product-form>, which
      // lives outside this element and would otherwise leak on a re-render.
      this.controller = new AbortController();
      const signal = this.controller.signal;

      const dataElement = this.querySelector('[data-packages-variants]');
      this.variants = dataElement ? JSON.parse(dataElement.textContent) : [];
      this.buildIndexes();

      this.tiers = Array.from(this.querySelectorAll('[data-packages-tier]'));
      this.inputs = Array.from(this.querySelectorAll('[data-packages-input]'));
      this.errorElement = this.querySelector('[data-packages-error]');
      this.gift = this.querySelector('[data-packages-gift]');
      this.ownButton = this.querySelector('[data-packages-submit]');
      this.ownButtonPrice = this.querySelector('[data-packages-submit-price]');

      const sectionId = this.dataset.sectionId;
      this.form = document.getElementById(`product-form-${sectionId}`);
      this.productForm = this.form ? this.form.closest('product-form') : null;
      this.submitPriceElement = document.getElementById(`ProductSubmitPrice-${sectionId}`);
      this.themeButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      this.quantityInput = document.getElementById(`Quantity-${sectionId}`);

      this.inputs.forEach((input) => {
        input.addEventListener('change', this.onTierChange.bind(this), { signal });
      });

      this.querySelectorAll('[data-packages-select]').forEach((select) => {
        select.addEventListener('change', this.onSelectChange.bind(this), { signal });
      });

      if (this.ownButton) {
        this.ownButton.addEventListener('click', this.onSubmit.bind(this), { signal });
      }

      if (this.usesThemeButton && this.productForm) {
        this.productForm.addEventListener('submit', this.onFormSubmit.bind(this), {
          capture: true,
          signal,
        });
      }

      // Labels and sold-out state have to be painted up front, not just on
      // change -- a single-option product resolves every price straight away.
      this.querySelectorAll('[data-packages-item]').forEach((item) => this.refreshOptions(item));

      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    /**
     * Two lookups built once, so neither pricing nor availability has to scan
     * the variant list: an exact-combination map for "which variant is this
     * row", and per-option buckets for "which variants carry this value".
     */
    buildIndexes() {
      this.variantByKey = new Map();
      this.optionBuckets = [];

      this.variants.forEach((variant) => {
        this.variantByKey.set(variant.options.join(SEPARATOR), variant);

        variant.options.forEach((value, index) => {
          if (!this.optionBuckets[index]) this.optionBuckets[index] = new Map();

          const bucket = this.optionBuckets[index];
          if (!bucket.has(value)) bucket.set(value, []);
          bucket.get(value).push(variant);
        });
      });

      this.cheapestVariant =
        this.variants
          .filter((variant) => variant.available)
          .reduce((cheapest, variant) => (!cheapest || variant.price < cheapest.price ? variant : cheapest), null) ||
        null;
    }

    /** Variants carrying `value` at `position` that also match every filled `others` select. */
    candidates(position, value, others) {
      const bucket = this.optionBuckets[position]?.get(value) || [];
      if (others.length === 0) return bucket;

      return bucket.filter((variant) =>
        others.every((other) => variant.options[Number(other.dataset.optionPosition) - 1] === other.value)
      );
    }

    get usesThemeButton() {
      return this.dataset.buttonMode !== 'own';
    }

    /** The button whose loading state this component drives. */
    get button() {
      return this.usesThemeButton ? this.themeButton : this.ownButton;
    }

    /** How many packages to add, from the section's quantity input. */
    get packageCount() {
      const value = parseInt(this.quantityInput?.value, 10);
      return Number.isNaN(value) || value < 1 ? 1 : value;
    }

    /** The radio of the currently selected tier, or null before any choice. */
    get selectedInput() {
      return this.inputs.find((input) => input.checked) || null;
    }

    get selectedTier() {
      const input = this.selectedInput;
      return input ? input.closest('[data-packages-tier]') : null;
    }

    onTierChange() {
      this.hideError();
      this.render();
    }

    onSelectChange(event) {
      this.hideError();
      this.refreshOptions(event.currentTarget.closest('[data-packages-item]'), event.currentTarget);
      this.render();
    }

    /**
     * Variant matching every option value already picked in this item row.
     * Returns null while the row is incomplete or the combination does not exist.
     */
    itemVariant(item) {
      const selects = Array.from(item.querySelectorAll('[data-packages-select]'));
      if (selects.some((select) => !select.value)) return null;

      const values = [];
      selects.forEach((select) => {
        values[Number(select.dataset.optionPosition) - 1] = select.value;
      });

      return this.variantByKey.get(values.join(SEPARATOR)) || null;
    }

    /** Cheapest available variant, used as the placeholder price for empty rows. */
    get fallbackVariant() {
      return this.cheapestVariant;
    }

    /**
     * Greys out option values that cannot be combined with the other selects in
     * the same row, so a shopper cannot build a variant that does not exist, and
     * writes each value's price into its label.
     *
     * A value is available when ANY variant carrying it is in stock, not merely
     * the first one found: on a Size + Colour product, M must stay selectable
     * while M/Gold is in stock even if M/Black -- which may come first in the
     * variant list -- has sold out.
     *
     * The price, by contrast, needs a single variant, so it is only shown once
     * the other selects in the row pin one down: "M" costs one thing in Black
     * and another in Gold. `keep` is the select the shopper just touched, which
     * is never the one cleared when two choices contradict each other.
     */
    refreshOptions(item, keep = null) {
      if (!item) return;

      const selects = Array.from(item.querySelectorAll('[data-packages-select]'));
      const others = (select) => selects.filter((other) => other !== select && other.value);
      const position = (select) => Number(select.dataset.optionPosition) - 1;

      // A choice made earlier can be invalidated by one made now. Left in place
      // it would keep displaying a value the row can no longer deliver, and only
      // fail at add-to-cart. Every select carries a real value from the server,
      // so this moves to another working one rather than blanking the field --
      // there is no placeholder option left to fall back to.
      selects.forEach((select) => {
        if (select === keep || !select.value) return;

        const worksWith = (value) =>
          this.candidates(position(select), value, others(select)).some((variant) => variant.available);

        if (worksWith(select.value)) return;

        const replacement = Array.from(select.options).find(
          (option) => option.value && worksWith(option.value)
        );

        select.value = replacement ? replacement.value : '';
      });

      selects.forEach((select) => {
        const siblings = others(select);
        const resolvesVariant = siblings.length === selects.length - 1;

        Array.from(select.options).forEach((option) => {
          if (!option.value) return;

          const matches = this.candidates(position(select), option.value, siblings);
          const inStock = matches.find((variant) => variant.available);

          option.disabled = !inStock;
          option.textContent = this.optionLabel(option.value, resolvesVariant ? inStock || matches[0] : null);
        });
      });
    }

    get showOptionPrices() {
      return this.dataset.showOptionPrices === 'true';
    }

    /**
     * "M — $54.99 (was $64.99)". A native <option> renders text only, so the
     * original price cannot be struck through; the prefix labels it instead.
     */
    optionLabel(value, variant) {
      if (!this.showOptionPrices || !variant) return value;

      let label = `${value} — ${this.formatMoney(variant.price)}`;
      const compare = Math.max(variant.compare_at_price || 0, variant.price);

      if (compare > variant.price) {
        label += ` (${this.dataset.comparePrefix || ''}${this.formatMoney(compare)})`;
      }

      return label;
    }

    /**
     * Totals for one tier: `chosen` is false while any item row is still empty.
     *
     * `total` is the plain sum of what the items cost. The block never discounts
     * it: a percentage off comes from a Shopify automatic discount and lands in
     * the cart, so taking it off here too would quote a price the checkout does
     * not charge. `compare` is built from compare_at_price, so a product already
     * on sale still shows its saving.
     */
    tierTotals(tier) {
      const items = Array.from(tier.querySelectorAll('[data-packages-item]'));
      const fallback = this.fallbackVariant;

      let compare = 0;
      let price = 0;
      let chosen = true;

      items.forEach((item) => {
        const variant = this.itemVariant(item);
        if (!variant) chosen = false;

        const source = variant || fallback;
        if (!source) return;

        price += source.price;
        compare += Math.max(source.compare_at_price || 0, source.price);
      });

      return {
        compare: compare,
        total: price,
        chosen: chosen,
        items: items,
      };
    }

    render() {
      const selected = this.selectedTier;
      let selectedTotals = null;

      this.tiers.forEach((tier) => {
        const isSelected = tier === selected;
        tier.classList.toggle('is-selected', isSelected);

        const options = tier.querySelector('[data-packages-options]');
        if (options) options.hidden = !isSelected;

        const totals = this.tierTotals(tier);
        if (isSelected) selectedTotals = totals;

        const totalElement = tier.querySelector('[data-packages-total]');
        const compareElement = tier.querySelector('[data-packages-compare]');

        if (totalElement) totalElement.textContent = this.formatMoney(totals.total);

        if (compareElement) {
          compareElement.textContent = this.formatMoney(totals.compare);
          compareElement.hidden = totals.compare <= totals.total;
        }
      });

      if (this.ownButtonPrice) {
        this.ownButtonPrice.textContent = selectedTotals ? this.formatMoney(selectedTotals.total) : '';
      }

      if (this.ownButton) this.ownButton.disabled = !selected;

      if (this.usesThemeButton && selectedTotals) this.renderThemePrice(selectedTotals);

      this.renderGift();
    }

    /**
     * The gift unlocks once the selected tier holds enough items. It is purely
     * presentational -- never a line item and never part of tierTotals -- so
     * unlocking it changes nothing about what the shopper is charged.
     */
    get giftUnlocked() {
      if (!this.gift) return false;

      const input = this.selectedInput;
      if (!input) return false;

      const min = Number(this.gift.dataset.minQuantity) || 1;
      return (Number(input.dataset.quantity) || 0) >= min;
    }

    renderGift() {
      if (!this.gift) return;

      const unlocked = this.giftUnlocked;
      this.gift.classList.toggle('is-unlocked', unlocked);
      this.gift.classList.toggle('is-locked', !unlocked);
    }

    /**
     * Rewrites the section's Add to cart price to the package total. The spans
     * carry data-unit-price rather than plain text so <product-submit-price>'s
     * own render() keeps multiplying by the quantity input; when the variant was
     * unavailable at page load the element is empty and the spans are built here.
     */
    renderThemePrice(totals) {
      const element = this.submitPriceElement;
      if (!element) return;

      let current = element.querySelector('.product-form__submit-price-current');

      if (!current) {
        const separator = document.createElement('span');
        separator.className = 'product-form__submit-price-separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.innerHTML = '&mdash;';

        current = document.createElement('span');
        current.className = 'product-form__submit-price-current';

        element.append(separator, current);
      }

      current.dataset.unitPrice = totals.total;

      let compare = element.querySelector('.product-form__submit-price-compare');

      if (totals.compare > totals.total) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'product-form__submit-price-compare';
          element.append(compare);
        }
        compare.dataset.unitPrice = totals.compare;
      } else if (compare) {
        compare.remove();
      }

      if (typeof element.render === 'function') {
        element.render();
      } else {
        current.textContent = this.formatMoney(totals.total * this.packageCount);
        if (compare) compare.textContent = this.formatMoney(totals.compare * this.packageCount);
      }
    }

    /** Capture-phase handler on <product-form>; keeps the theme's own submit from running. */
    onFormSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      this.onSubmit();
    }

    onSubmit() {
      const tier = this.selectedTier;
      if (!tier) {
        this.showError(this.dataset.errorIncomplete);
        return;
      }

      const totals = this.tierTotals(tier);
      if (!totals.chosen) {
        this.showError(this.dataset.errorIncomplete);
        return;
      }

      const variants = totals.items.map((item) => this.itemVariant(item));
      if (variants.some((variant) => !variant || !variant.available)) {
        this.showError(this.dataset.errorUnavailable);
        return;
      }

      const input = this.selectedInput;
      const packageName = tier.querySelector('.product-packages__title')?.textContent.trim() || '';
      const count = this.packageCount;

      // One line item per distinct variant; identical sizes are merged so the
      // cart shows "Size M x 2" rather than two separate lines. `_package` keeps
      // its underscore so it stays internal to every line.
      const items = [];
      variants.forEach((variant) => {
        const existing = items.find((item) => item.id === variant.id);
        if (existing) {
          existing.quantity += count;
          return;
        }

        items.push({
          id: variant.id,
          quantity: count,
          properties: packageName ? { _package: packageName } : {},
        });
      });

      // The gift is one per package, not one per item: a package of two earns a
      // single gift, so the note goes on the first line only rather than being
      // repeated down the cart as if each item came with its own.
      if (this.giftUnlocked && items.length > 0) {
        const label = this.gift.dataset.noteLabel;
        const value = this.gift.dataset.noteValue;

        if (label && value) {
          // Buying several packages earns several gifts, which the seller can
          // only know from the note.
          items[0].properties[label] = count > 1 ? `${value} x ${count}` : value;
        }
      }

      items.forEach((item) => {
        if (Object.keys(item.properties).length === 0) delete item.properties;
      });

      this.addToCart(items);
    }

    addToCart(items) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: items };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      this.setLoading(true);

      fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            return null;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-packages', cartData: response });
          }

          cart.renderContents(response);
        })
        .catch(() => {
          this.showError(window.cartStrings ? window.cartStrings.error : '');
        })
        .finally(() => {
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          this.setLoading(false);
        });
    }

    setLoading(isLoading) {
      const button = this.button;
      if (!button) return;

      button.classList.toggle('loading', isLoading);
      button.setAttribute('aria-disabled', isLoading);

      // The theme's button keeps its disabled state tied to variant availability,
      // so only the block's own button is toggled here.
      if (!this.usesThemeButton) button.disabled = isLoading || !this.selectedInput;

      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      if (!this.errorElement || !message) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    hideError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    /**
     * Degrades instead of throwing: money-format.js failing to load must not
     * take tier selection down with it, since render() runs on every change.
     */
    formatMoney(cents) {
      const format = window.Theme?.formatMoney;
      return format ? format(cents, this.dataset.moneyFormat) : (cents / 100).toFixed(2);
    }
  }

  customElements.define('product-packages', ProductPackages);
}
